import {
  Activity,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Columns2,
  Compass,
  Flag,
  Layers,
  List,
  Megaphone,
  MessageSquare,
  Mic,
  Newspaper,
  Search,
  Shield,
  Store,
  Users,
  Volume2,
} from "lucide-react";

import type { GtmBrainDoc } from "../api/GtmApi";
import type { BrainCategoryId, BrainKnowledgeRow } from "./brainToFlow";
import { listCompetitors } from "./competitors";
import { listInfluencers } from "./influencers";
import { listKeywords } from "./keywords";
import { MARKET_PROFILE_SUBNAV, type MarketProfileDocId } from "./marketProfile";
import { listObjections } from "./objections";
import { listPersonas } from "./personas";
import { listPillars } from "./pillars";
import { SIGNALS_SUBNAV, type SignalDocId } from "./signalsProfile";
import { VOICE_SUBNAV, type VoiceDocId } from "./voiceProfile";

type Props = {
  rows: BrainKnowledgeRow[];
  docs: GtmBrainDoc[];
  selectedCategoryId?: BrainCategoryId | null;
  selectedMarketDocId?: MarketProfileDocId | null;
  selectedPersonaKey?: string | null;
  selectedPillarKey?: string | null;
  selectedObjectionKey?: string | null;
  selectedCompetitorKey?: string | null;
  selectedInfluencerKey?: string | null;
  selectedVoiceDocId?: VoiceDocId | null;
  selectedSignalDocId?: SignalDocId | null;
  query: string;
  onQueryChange: (q: string) => void;
  onSelectCategory: (id: BrainCategoryId) => void;
  onSelectMarketDoc?: (id: MarketProfileDocId) => void;
  onSelectPersona?: (key: string) => void;
  onSelectPillar?: (key: string) => void;
  onSelectObjection?: (key: string) => void;
  onSelectCompetitor?: (key: string) => void;
  onSelectInfluencer?: (key: string) => void;
  onSelectVoiceDoc?: (id: VoiceDocId) => void;
  onSelectSignalDoc?: (id: SignalDocId) => void;
  onBrainOverview: () => void;
};

export default function BrainKnowledgeSidebar({
  rows,
  docs,
  selectedCategoryId,
  selectedMarketDocId,
  selectedPersonaKey,
  selectedPillarKey,
  selectedObjectionKey,
  selectedCompetitorKey,
  selectedInfluencerKey,
  selectedVoiceDocId,
  selectedSignalDocId,
  query,
  onQueryChange,
  onSelectCategory,
  onSelectMarketDoc,
  onSelectPersona,
  onSelectPillar,
  onSelectObjection,
  onSelectCompetitor,
  onSelectInfluencer,
  onSelectVoiceDoc,
  onSelectSignalDoc,
  onBrainOverview,
}: Props) {
  const personas = listPersonas(docs);
  const pillars = listPillars(docs);
  const objections = listObjections(docs);
  const competitors = listCompetitors(docs);
  const influencers = listInfluencers(docs);
  const keywords = listKeywords(docs);
  const q = query.trim().toLowerCase();

  const filtered = rows.filter((r) => {
    if (!q) return true;
    if (r.label.toLowerCase().includes(q)) return true;
    if (r.id === "market") {
      return MARKET_PROFILE_SUBNAV.some((s) => s.label.toLowerCase().includes(q));
    }
    if (r.id === "personas") {
      return personas.some((p) => p.label.toLowerCase().includes(q));
    }
    if (r.id === "pillars") {
      return pillars.some((p) => p.label.toLowerCase().includes(q));
    }
    if (r.id === "objections") {
      return objections.some((o) => o.label.toLowerCase().includes(q));
    }
    if (r.id === "competitors") {
      return competitors.some((c) => c.label.toLowerCase().includes(q));
    }
    if (r.id === "influencers") {
      return influencers.some((i) => i.label.toLowerCase().includes(q));
    }
    if (r.id === "keywords") {
      return keywords.some((k) => k.phrase.toLowerCase().includes(q));
    }
    if (r.id === "voice") {
      return VOICE_SUBNAV.some((s) => s.label.toLowerCase().includes(q));
    }
    if (r.id === "signals") {
      return SIGNALS_SUBNAV.some((s) => s.label.toLowerCase().includes(q));
    }
    return false;
  });

  const marketExpanded = selectedCategoryId === "market";
  const personasExpanded = selectedCategoryId === "personas";
  const pillarsExpanded = selectedCategoryId === "pillars";
  const voiceExpanded = selectedCategoryId === "voice";
  const signalsExpanded = selectedCategoryId === "signals";
  const objectionsExpanded = selectedCategoryId === "objections";
  const competitorsExpanded = selectedCategoryId === "competitors";
  const influencersExpanded = selectedCategoryId === "influencers";

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-50/80">
      <div className="border-b border-slate-200 px-3 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search your Brain…"
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
          />
        </div>
      </div>

      <div className="px-3 py-2">
        <button
          type="button"
          className={`w-full rounded-md px-2.5 py-1.5 text-left text-xs font-semibold ${
            !selectedCategoryId
              ? "bg-sky-50 text-sky-900"
              : "text-slate-700 hover:bg-white/80"
          }`}
          onClick={onBrainOverview}
        >
          Brain overview
        </button>
      </div>

      <div className="px-3 pb-1 pt-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Knowledge
        </p>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        <ul className="space-y-0.5">
          {filtered.map((row) => {
            const active = selectedCategoryId === row.id;
            const isMarket = row.id === "market";
            const isPersonas = row.id === "personas";
            const isPillars = row.id === "pillars";
            const isObjections = row.id === "objections";
            const isCompetitors = row.id === "competitors";
            const isInfluencers = row.id === "influencers";
            const isVoice = row.id === "voice";
            const isSignals = row.id === "signals";
            const isKeywords = row.id === "keywords";
            return (
              <li key={row.id}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                    active
                      ? isPersonas
                        ? "bg-violet-50 font-semibold text-violet-900 ring-1 ring-violet-100"
                        : isPillars
                          ? "bg-blue-50 font-semibold text-blue-900 ring-1 ring-blue-100"
                          : isObjections
                            ? "bg-sky-50 font-semibold text-sky-900 ring-1 ring-sky-100"
                            : isCompetitors
                              ? "bg-orange-50 font-semibold text-orange-900 ring-1 ring-orange-100"
                              : isInfluencers
                                ? "bg-amber-50 font-semibold text-amber-900 ring-1 ring-amber-100"
                                : isVoice
                                  ? "bg-teal-50 font-semibold text-teal-900 ring-1 ring-teal-100"
                                  : isSignals
                                    ? "bg-amber-50 font-semibold text-amber-950 ring-1 ring-amber-100"
                                    : isKeywords
                                      ? "bg-lime-50 font-semibold text-lime-900 ring-1 ring-lime-100"
                                      : "bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-700 hover:bg-white/80"
                  }`}
                  onClick={() => onSelectCategory(row.id)}
                >
                  {isMarket ? (
                    <Store className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                  ) : isPersonas ? (
                    <Users className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                  ) : isPillars ? (
                    <Columns2 className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                  ) : isObjections ? (
                    <Shield className="h-3.5 w-3.5 shrink-0 text-sky-600" />
                  ) : isCompetitors ? (
                    <Compass className="h-3.5 w-3.5 shrink-0 text-orange-600" />
                  ) : isInfluencers ? (
                    <Megaphone className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  ) : isKeywords ? (
                    <List className="h-3.5 w-3.5 shrink-0 text-lime-700" />
                  ) : isVoice ? (
                    <Volume2 className="h-3.5 w-3.5 shrink-0 text-teal-600" />
                  ) : isSignals ? (
                    <Activity className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                  ) : (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: row.color }}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {row.count > 0 ? (
                    <span className="tabular-nums text-[10px] text-slate-400">{row.count}</span>
                  ) : null}
                  {isMarket ||
                  isPersonas ||
                  isPillars ||
                  isObjections ||
                  isCompetitors ||
                  isInfluencers ||
                  isVoice ||
                  isSignals ? (
                    active ? (
                      <ChevronDown className="h-3 w-3 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-slate-400" />
                    )
                  ) : null}
                </button>
                {isMarket && marketExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {MARKET_PROFILE_SUBNAV.map((sub) => {
                      const subActive = selectedMarketDocId === sub.id;
                      return (
                        <li key={sub.id}>
                          <button
                            type="button"
                            className={`w-full rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-rose-50 font-medium text-rose-800"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectMarketDoc?.(sub.id)}
                          >
                            {sub.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isPersonas && personasExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {personas.map((p) => {
                      const subActive = selectedPersonaKey === p.key;
                      return (
                        <li key={p.key}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-violet-50 font-medium text-violet-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectPersona?.(p.key)}
                          >
                            <span className="shrink-0 text-[10px]">{p.emoji}</span>
                            <span className="truncate">{p.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isPillars && pillarsExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {pillars.map((p) => {
                      const subActive = selectedPillarKey === p.key;
                      return (
                        <li key={p.key}>
                          <button
                            type="button"
                            className={`flex w-full items-start gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-blue-50 font-medium text-blue-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectPillar?.(p.key)}
                          >
                            <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-blue-500" />
                            <span className="line-clamp-2">{p.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isObjections && objectionsExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {objections.map((o) => {
                      const subActive = selectedObjectionKey === o.key;
                      return (
                        <li key={o.key}>
                          <button
                            type="button"
                            className={`flex w-full items-start gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-sky-50 font-medium text-sky-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectObjection?.(o.key)}
                          >
                            <Shield className="mt-0.5 h-3 w-3 shrink-0 text-rose-500" />
                            <span className="line-clamp-2">{o.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isCompetitors && competitorsExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {competitors.map((c) => {
                      const subActive = selectedCompetitorKey === c.key;
                      return (
                        <li key={c.key}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-orange-50 font-medium text-orange-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectCompetitor?.(c.key)}
                          >
                            <Flag className="h-3 w-3 shrink-0 text-orange-500" />
                            <span className="truncate">{c.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isInfluencers && influencersExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {influencers.map((inf) => {
                      const subActive = selectedInfluencerKey === inf.key;
                      return (
                        <li key={inf.key}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-amber-50 font-medium text-amber-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectInfluencer?.(inf.key)}
                          >
                            <Megaphone className="h-3 w-3 shrink-0 text-amber-500" />
                            <span className="truncate">{inf.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isVoice && voiceExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {VOICE_SUBNAV.map((sub) => {
                      const subActive = selectedVoiceDocId === sub.id;
                      const SubIcon =
                        sub.id === "linkedin" ? Briefcase : sub.id === "blog" ? Newspaper : Mic;
                      return (
                        <li key={sub.id}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-teal-50 font-medium text-teal-900"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectVoiceDoc?.(sub.id)}
                          >
                            <SubIcon className="h-3 w-3 shrink-0 text-teal-500" />
                            <span className="truncate">{sub.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {isSignals && signalsExpanded ? (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {SIGNALS_SUBNAV.map((sub) => {
                      const subActive = selectedSignalDocId === sub.id;
                      const SubIcon = sub.id === "stack" ? Layers : Users;
                      return (
                        <li key={sub.id}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] ${
                              subActive
                                ? "bg-amber-50 font-medium text-amber-950"
                                : "text-slate-600 hover:bg-white/80"
                            }`}
                            onClick={() => onSelectSignalDoc?.(sub.id)}
                          >
                            <SubIcon className="h-3 w-3 shrink-0 text-amber-700" />
                            <span className="truncate">{sub.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
